/* eslint-disable i18next/no-literal-string */
import {Badge} from '@dropbox/dig-components/badge';
import {Button} from '@dropbox/dig-components/buttons';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, SearchLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {
  type ScorecardMetricDirectionOut,
  type ScorecardMetricDirectionsResponse,
  ScorecardsService,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser, isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';

import styles from './AdminScorecardMetricDirections.module.css';

const directionsQueryKey = ['admin', 'scorecards', 'metric-directions'] as const;

type DirectionFilter = 'all' | 'unknown' | 'overridden';
type DirectionChoice = 'auto' | 'higher' | 'lower';

const DIRECTION_LABELS: Record<DirectionChoice, string> = {
  auto: 'Automatic',
  higher: 'Higher is better',
  lower: 'Lower is better',
};

const INFERRED_LABELS: Record<
  NonNullable<ScorecardMetricDirectionOut['inferred_direction']>,
  string
> = {
  higher: 'Higher',
  lower: 'Lower',
  unknown: 'Can’t tell',
};

const displayValue = (value?: string | null) => value?.trim() || '-';

const matchesSearch = (metric: ScorecardMetricDirectionOut, query: string) => {
  if (!query) return true;
  const haystack = [
    metric.display_name,
    metric.source_workstream_name ?? '',
    ...(metric.section_labels ?? []),
  ]
    .join(' ')
    .toLocaleLowerCase();
  return haystack.includes(query);
};

const matchesFilter = (metric: ScorecardMetricDirectionOut, filter: DirectionFilter) => {
  if (filter === 'unknown') return metric.inferred_direction === 'unknown';
  if (filter === 'overridden') return Boolean(metric.desired_direction);
  return true;
};

const DirectionControl = ({metric}: {metric: ScorecardMetricDirectionOut}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (desiredDirection: ScorecardMetricDirectionOut['desired_direction']) =>
      getService(
        ScorecardsService
      ).updateScorecardMetricDirectionApiV1AdminScorecardsMetricDirectionsMetricIdPut(
        metric.metric_id,
        {desired_direction: desiredDirection}
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData<ScorecardMetricDirectionsResponse>(directionsQueryKey, (data) =>
        data
          ? {
              ...data,
              metrics: data.metrics?.map((row) =>
                row.metric_id === updated.metric_id ? updated : row
              ),
            }
          : data
      );
    },
  });

  if (metric.is_redacted) {
    return (
      <Text size="small" color="subtle">
        Confidential
      </Text>
    );
  }

  const selected: DirectionChoice = metric.desired_direction ?? 'auto';
  const inferredLabel = metric.inferred_direction
    ? INFERRED_LABELS[metric.inferred_direction].toLocaleLowerCase()
    : null;
  const choiceDescription: Record<DirectionChoice, string> = {
    auto: inferredLabel
      ? `Inferred from baseline and plan: ${inferredLabel}`
      : 'Inferred from baseline and plan',
    higher: 'Beating budget or target means a larger value',
    lower: 'Beating budget or target means a smaller value',
  };

  return (
    <Stack gap="4">
      <Menu.Wrapper>
        {({getContentProps, getTriggerProps}) => (
          <>
            <Button
              variant={selected === 'auto' ? 'outline' : 'primary'}
              size="small"
              withDropdownIcon
              disabled={mutation.isPending}
              aria-label={`Direction for ${metric.display_name}: ${DIRECTION_LABELS[selected]}`}
              {...getTriggerProps()}
            >
              {DIRECTION_LABELS[selected]}
            </Button>
            <Menu.Content {...getContentProps()} placement="bottom-start" style={{minWidth: 280}}>
              <Menu.Segment withLabel="Which direction is good?">
                {(Object.keys(DIRECTION_LABELS) as DirectionChoice[]).map((choice) => (
                  <Menu.ActionItem
                    key={choice}
                    onClick={() => {
                      if (choice !== selected) {
                        mutation.mutate(choice === 'auto' ? null : choice);
                      }
                    }}
                    withSubtitle={choiceDescription[choice]}
                    withRightAccessory={
                      choice === selected ? <UIIcon src={CheckmarkLine} /> : undefined
                    }
                  >
                    {DIRECTION_LABELS[choice]}
                  </Menu.ActionItem>
                ))}
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
      {mutation.isPending ? (
        <Text size="small" color="subtle" role="status">
          Saving…
        </Text>
      ) : mutation.isError ? (
        <Text size="small" color="subtle" role="alert">
          Unable to save. Try again.
        </Text>
      ) : null}
    </Stack>
  );
};

const MetricDirectionRow = ({metric}: {metric: ScorecardMetricDirectionOut}) => {
  const isUnknown = metric.inferred_direction === 'unknown';
  return (
    <Table.Row data-testid={`metric-direction-row-${metric.metric_id}`}>
      <Table.Cell>
        <Stack gap="4">
          <Box>
            <Text isBold>{metric.display_name}</Text>
          </Box>
          {metric.source_workstream_name ? (
            <Box>
              <Text size="small" color="subtle">
                {metric.source_workstream_name}
              </Text>
            </Box>
          ) : null}
        </Stack>
      </Table.Cell>
      <Table.Cell>
        <Text>{(metric.section_labels ?? []).join(', ')}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text>{metric.is_redacted ? '-' : displayValue(metric.baseline)}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text>
          {metric.is_redacted
            ? '-'
            : `${displayValue(metric.current_quarter_budget)} / ${displayValue(
                metric.current_quarter_target
              )}`}
        </Text>
      </Table.Cell>
      <Table.Cell>
        {isUnknown ? (
          <Badge tone="warning">{INFERRED_LABELS.unknown}</Badge>
        ) : (
          <Text>
            {metric.inferred_direction ? INFERRED_LABELS[metric.inferred_direction] : '-'}
          </Text>
        )}
      </Table.Cell>
      <Table.Cell>
        <DirectionControl metric={metric} />
      </Table.Cell>
    </Table.Row>
  );
};

export const AdminScorecardMetricDirections = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const canView = isUserAdmin(pulseUser) || isSuperUser(pulseUser);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<DirectionFilter>('all');

  useDocumentTitle('Scorecard metric direction');

  const {data, isError, isLoading} = useQuery({
    queryKey: directionsQueryKey,
    queryFn: () =>
      getService(
        ScorecardsService
      ).listScorecardMetricDirectionsApiV1AdminScorecardsMetricDirectionsGet(),
    enabled: canView,
  });

  const metrics = useMemo(() => data?.metrics ?? [], [data]);
  const quarterLabel = data?.period?.slice(4) ?? 'Quarter';
  const columns = useMemo<Column<ScorecardMetricDirectionOut>[]>(
    () => [
      {type: 'Metric', minWidth: 320},
      {type: 'Scorecard tabs', minWidth: 160},
      {type: 'Baseline', fixed: true, minWidth: 100},
      {type: `${quarterLabel} budget / target`, fixed: true, minWidth: 160},
      {type: 'Inferred', fixed: true, minWidth: 110},
      {type: 'Direction', fixed: true, minWidth: 200},
    ],
    [quarterLabel]
  );
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleMetrics = metrics.filter(
    (metric) => matchesFilter(metric, filter) && matchesSearch(metric, normalizedSearch)
  );
  const unknownCount = metrics.filter((metric) => matchesFilter(metric, 'unknown')).length;
  const overriddenCount = metrics.filter((metric) => matchesFilter(metric, 'overridden')).length;

  if (!pulseUser) return null;
  if (!canView) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {
          children: 'Scorecard metric direction',
          to: ROUTE_PATHS.ADMIN_SCORECARD_METRIC_DIRECTIONS,
        },
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title size={24}>Scorecard metric direction</Title>
          <Text color="subtle">
            Choose whether higher or lower is better for metrics on Scorecards. At quarter close,
            budget and target cells are colored by whether the actual beat them in this direction.
            Automatic infers it from the baseline and plans, and can’t tell when they are all equal.
          </Text>
        </Stack>

        <Split gap="12" alignY="center" wrap="wrap">
          <Split.Item>
            <TextInput
              size="small"
              className={styles.search}
              aria-label="Search scorecard metrics"
              placeholder="Search metrics, workstreams, or tabs"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              withLeftAccessory={<UIIcon src={SearchLine} />}
            />
          </Split.Item>
          <Split.Item>
            <SegmentedControl
              selectedValue={filter}
              onSelection={(value) => value && setFilter(value as DirectionFilter)}
            >
              <SegmentedControl.Option variant="text" value="all">
                All
              </SegmentedControl.Option>
              <SegmentedControl.Option variant="text" value="unknown">
                {`Can’t infer · ${unknownCount}`}
              </SegmentedControl.Option>
              <SegmentedControl.Option variant="text" value="overridden">
                {`Overridden · ${overriddenCount}`}
              </SegmentedControl.Option>
            </SegmentedControl>
          </Split.Item>
        </Split>

        {isError ? (
          <SystemBanner tone="alert" role="alert">
            <Text>Unable to load scorecard metrics. Try refreshing the page.</Text>
          </SystemBanner>
        ) : null}

        <Table columns={columns} data={visibleMetrics} compact>
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Split gap="8" alignY="center">
                    <Spinner size="small" />
                    <Text color="subtle">Loading scorecard metrics</Text>
                  </Split>
                </Table.Cell>
              </Table.Row>
            ) : isError ? null : visibleMetrics.length ? (
              visibleMetrics.map((metric) => (
                <MetricDirectionRow key={metric.metric_id} metric={metric} />
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box paddingY="16">
                    <Text color="subtle">No scorecard metrics match.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Stack>
    </Layout.Container>
  );
};
